import { NewActionMenu } from "@/features/actions";
import { BriefingSlot } from "@/features/briefing";
import { capabilityManifestQueryOptions } from "@/features/capabilities";
import { useChatUserId } from "@/features/chat";
import { useGoToIntegrations } from "@/features/connectors";
import { FocusedViewLayout, clearFocusKeepMsg } from "@/features/sources";
import { CalendarEventPane } from "@/features/sources/components/CalendarEventPane";
import { validateCalendarSearch } from "@/features/workspace-router";
import { CalendarSearchFilters } from "@/features/workspace/components/searchFilters";
import { useActiveConnections } from "@/features/workspace/hooks/useActiveConnections";
import {
  calendarConnectionsQueryOptions,
  useCalendarConnections,
  useUpcomingCalendarEvents,
} from "@/features/workspace/hooks/useCalendar";
import { useSearchNavigation } from "@/features/workspace/hooks/useSearchNavigation";
import type { CalendarConnection, CalendarEvent } from "@ando/core";
import {
  Button,
  ConnectionPicker,
  type ConnectionPickerOption,
  SegmentedTabs,
  SurfaceCommandBar,
  SurfaceEmptyState,
  SurfaceFiltersPopover,
  SurfaceLayout,
  WorkspaceSearchBar,
  resolveConnectionColor,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Link as LinkIcon,
  Settings2,
  Sparkles,
} from "lucide-react";
import type * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/_authed/workspace/calendar")({
  validateSearch: validateCalendarSearch,
  // Warm the calendar-connections list on hover/focus (intent preload) so the
  // hub's prerequisite fetch (usually the slow one) resolves from cache. The
  // key is a pure constant, so we preload unconditionally — the hook keeps the
  // ``calendar_oauth`` capability gate. Fire-and-forget: a slow/failed prefetch
  // must never block navigation. The events fan-out
  // (``useUpcomingCalendarEvents``) depends on the fetched connections at
  // runtime and is intentionally left out. We also warm the capability manifest
  // so the hub's capability gate resolves from cache on first paint.
  loader: ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData(calendarConnectionsQueryOptions());
    void queryClient.ensureQueryData(capabilityManifestQueryOptions());
  },
  component: CalendarHub,
});

// ── Range model ──────────────────────────────────────────────────────────

type CalendarRange = "day" | "week" | "month";

const RANGE_STORAGE_KEY = "ando.calendar.range";

/** Read the persisted range from localStorage; falls back to ``"week"``. */
function readPersistedRange(): CalendarRange {
  if (typeof window === "undefined") return "week";
  try {
    const raw = window.localStorage.getItem(RANGE_STORAGE_KEY);
    if (raw === "day" || raw === "week" || raw === "month") return raw;
  } catch {
    // localStorage may be unavailable (private mode / SSR) — silently fall back.
  }
  return "week";
}

// ── Date helpers ────────────────────────────────────────────────────────

/** Italian short day label — Mon..Sun (`getDay`: 1..0 with Sun=0). */
const DAY_LABELS_IT: readonly string[] = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const MONTHS_IT: readonly string[] = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

const MONTHS_IT_SHORT: readonly string[] = [
  "Gen",
  "Feb",
  "Mar",
  "Apr",
  "Mag",
  "Giu",
  "Lug",
  "Ago",
  "Set",
  "Ott",
  "Nov",
  "Dic",
];

/** "YYYY-MM-DD" key — local date, NOT UTC (Mon..Sun grid is user-local). */
function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** "HH:MM" formatter — locale-stable, 24h. */
function timeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * Adaptive "time-until-next-event" formatter (Italian).
 *
 * Rationale (May 22, 2026): the previous ``X min`` rendering was unusable
 * for events far out — "511 min" reads as noise, the user has to do
 * mental math. SOTA calendar UX (Google Calendar / Notion Calendar /
 * Fantastical) instead switches granularity to the largest meaningful unit:
 *
 *  - < 1 min            → "ora"           (a momento)
 *  - 1..59 min          → "12 min"        (precise minute count)
 *  - 1..23 hours        → "2h 30 min"     (compound, drop minutes when 0)
 *  - 1..6 days          → "3g 4h"         (compound, drop hours when 0)
 *  - ≥ 7 days           → "12g"           (just days, the calendar grid
 *                                          itself shows the date)
 *
 * Returns a short string suitable for inline rendering inside the header
 * meta strip and the CompanionHero. Never returns negative values — the
 * caller clamps at 0 via ``Math.max(0, …)``.
 */
function formatTimeUntil(totalMinutes: number, i18n: ReturnType<typeof useLingui>["i18n"]): string {
  if (totalMinutes < 1) return i18n._(msg`ora`);
  if (totalMinutes < 60) return i18n._(msg`${totalMinutes} min`);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) {
    return minutes === 0 ? i18n._(msg`${hours}h`) : i18n._(msg`${hours}h ${minutes} min`);
  }
  const days = Math.floor(hours / 24);
  const remainderHours = hours % 24;
  if (days < 7) {
    return remainderHours === 0 ? i18n._(msg`${days}g`) : i18n._(msg`${days}g ${remainderHours}h`);
  }
  return i18n._(msg`${days}g`);
}

interface DayCell {
  readonly day: string;
  readonly date: number;
  readonly today: boolean;
  readonly weekend: boolean;
  /** YYYY-MM-DD key used to group events. */
  readonly key: string;
  /** Full ISO snapshot of midnight-local for downstream re-construction. */
  readonly iso: string;
  /** ``true`` when this cell belongs to the current month (Month view only). */
  readonly inMonth: boolean;
}

/** Monday-anchored 7-day cells centered on ``anchor``. */
function buildWeek(anchor: Date): readonly DayCell[] {
  const dow = anchor.getDay();
  const monOffset = (dow + 6) % 7;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() - monOffset);
  monday.setHours(0, 0, 0, 0);

  const todayKey = dateKey(new Date());
  const anchorMonth = anchor.getMonth();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      day: DAY_LABELS_IT[i] ?? "",
      date: d.getDate(),
      today: dateKey(d) === todayKey,
      weekend: i === 5 || i === 6,
      key: dateKey(d),
      iso: d.toISOString(),
      inMonth: d.getMonth() === anchorMonth,
    };
  });
}

/** 6×7 grid covering ``anchor.getMonth()`` — Mon-anchored, full weeks. */
function buildMonth(anchor: Date): readonly DayCell[] {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const dow = firstOfMonth.getDay();
  const monOffset = (dow + 6) % 7;
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(firstOfMonth.getDate() - monOffset);
  gridStart.setHours(0, 0, 0, 0);

  const todayKey = dateKey(new Date());
  const anchorMonth = anchor.getMonth();
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const idxInWeek = i % 7;
    return {
      day: DAY_LABELS_IT[idxInWeek] ?? "",
      date: d.getDate(),
      today: dateKey(d) === todayKey,
      weekend: idxInWeek === 5 || idxInWeek === 6,
      key: dateKey(d),
      iso: d.toISOString(),
      inMonth: d.getMonth() === anchorMonth,
    };
  });
}

/** Human title for the current range window (Italian) shown between the
 *  prev/next arrows: "Gio 24 Luglio" · "21–27 Luglio" · "Luglio 2026". */
function formatRangeTitle(range: CalendarRange, anchor: Date): string {
  if (range === "day") {
    const wd = DAY_LABELS_IT[(anchor.getDay() + 6) % 7] ?? "";
    return `${wd} ${anchor.getDate()} ${MONTHS_IT[anchor.getMonth()]}`;
  }
  if (range === "week") {
    const dow = (anchor.getDay() + 6) % 7;
    const monday = new Date(anchor);
    monday.setDate(anchor.getDate() - dow);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    if (monday.getMonth() === sunday.getMonth()) {
      return `${monday.getDate()}–${sunday.getDate()} ${MONTHS_IT[monday.getMonth()]}`;
    }
    return `${monday.getDate()} ${MONTHS_IT_SHORT[monday.getMonth()]} – ${sunday.getDate()} ${MONTHS_IT_SHORT[sunday.getMonth()]}`;
  }
  return `${MONTHS_IT[anchor.getMonth()]} ${anchor.getFullYear()}`;
}

/** True when today falls inside the current range window — drives the
 *  disabled state of the "Oggi" button so it never no-ops. */
function isTodayVisible(range: CalendarRange, anchor: Date): boolean {
  const today = new Date();
  if (range === "day") return dateKey(anchor) === dateKey(today);
  if (range === "month") {
    return anchor.getFullYear() === today.getFullYear() && anchor.getMonth() === today.getMonth();
  }
  const mondayKey = (d: Date): string => {
    const m = new Date(d);
    m.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return dateKey(m);
  };
  return mondayKey(anchor) === mondayKey(today);
}

// ── Connection mapping ──────────────────────────────────────────────────

/** Default colour applied to event chips when the source connection is
 * unknown — keeps the Ando accent visible so unattributed events still
 * read as "Ando-managed", not orphaned. */
const ANDO_ACCENT_HEX = "#FF7A1A";

/**
 * Normalise the BE provider tag (``"google_calendar"`` /
 * May 21, 2026 — `normaliseCalendarProvider` removed. The redesigned
 * ConnectionPicker resolves raw vendor slugs (``google_calendar``/
 * ``outlook_calendar``) directly via its internal ``PROVIDER_TO_DOMAIN``
 * dict to ``domain.calendar`` and renders the matching ``Calendar`` glyph.
 * Passing through the raw provider is simpler AND avoids the cross-surface
 * leakage where mapping to "google"/"microsoft" caused other surfaces
 * (conversations/documents) to fall back to the wrong domain glyph.
 *
 * ``avatar_url`` / ``color_hex`` are not part of the BE payload yet — when
 * the connector wizard starts emitting per-account avatars / colours, just
 * forward them through here. ``kind`` lives on the ``surfaceId`` axis of
 * ``useActiveConnections`` (``"calendar"``), not on the picker option. */
function connectionToOption(c: CalendarConnection): ConnectionPickerOption {
  return {
    id: c.id,
    provider: c.provider,
    account_label: c.email_address,
  };
}

// ── Event grouping ──────────────────────────────────────────────────────

interface GridEvent {
  readonly id: string;
  readonly time: string;
  readonly title: string;
  /** When the event is the user's NEXT upcoming meeting, render with the
   * orange highlight ring. */
  readonly highlight: boolean;
  readonly meta?: string;
  /** Original BE event — passed to the drawer when the cell is clicked. */
  readonly raw: CalendarEvent;
  /** Source connection id (resolved from the upcoming-events bundle) —
   * ``null`` for unattributed events. Drives the left-border colour hint. */
  readonly connectionId: string | null;
  /** Resolved brand colour used for the left-border accent on the chip. */
  readonly color: string;
}

interface AttributedEvent {
  readonly event: CalendarEvent;
  readonly connectionId: string;
  /**
   * Tutte le connection (account calendar) che vedono questo meeting.
   * Quando l'utente ha 2+ account calendar e lo STESSO meeting appare
   * in più di uno (caso normale di meeting cross-account: organizer
   * ha invitato sia il tuo personal che il tuo work account), facciamo
   * un dedup logico → UN'unica entry visiva ma manteniamo la lista
   * completa così l'UI può rendere "👥 N account" come attribution.
   */
  readonly allConnectionIds: readonly string[];
}

function buildGridByDay(
  events: readonly AttributedEvent[],
  nextEventId: string | null,
  colorByConnectionId: (id: string | null) => string,
  untitledLabel: string,
): Record<string, GridEvent[]> {
  const map: Record<string, GridEvent[]> = {};
  for (const { event: e, connectionId } of events) {
    const t = Date.parse(e.start_time);
    if (Number.isNaN(t)) continue;
    const k = dateKey(new Date(t));
    if (!map[k]) map[k] = [];
    map[k].push({
      id: e.event_id,
      time: timeLabel(e.start_time),
      title: e.title || untitledLabel,
      highlight: nextEventId !== null && e.event_id === nextEventId,
      meta: e.location ?? undefined,
      raw: e,
      connectionId,
      color: colorByConnectionId(connectionId),
    });
  }
  return map;
}

/**
 * Millisecond ``[start, end)`` window covering EXACTLY what the given view
 * renders. Single source of truth for both the in-view event filter and the
 * BE fetch window, so navigating to any period fetches — and therefore shows
 * — that period's events (a real, browsable calendar, not just today ± n).
 */
function rangeBounds(
  range: CalendarRange,
  anchor: Date,
): { readonly start: number; readonly end: number } {
  if (range === "day") {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()).getTime();
    return { start, end: start + 24 * 3600_000 };
  }
  if (range === "week") {
    const dow = (anchor.getDay() + 6) % 7;
    const start =
      new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()).getTime() -
      dow * 24 * 3600_000;
    return { start, end: start + 7 * 24 * 3600_000 };
  }
  // month — full calendar grid (6×7) so off-month cells are covered too.
  const grid = buildMonth(anchor);
  const first = grid[0];
  const last = grid[grid.length - 1];
  const start = first
    ? Date.parse(first.iso)
    : new Date(anchor.getFullYear(), anchor.getMonth(), 1).getTime();
  const end = last ? Date.parse(last.iso) + 24 * 3600_000 : start + 42 * 24 * 3600_000;
  return { start, end };
}

/** Filter the upcoming feed to the events visible inside the current view. */
function filterByRange(
  events: readonly AttributedEvent[],
  range: CalendarRange,
  anchor: Date,
): readonly AttributedEvent[] {
  const { start, end } = rangeBounds(range, anchor);
  return events.filter(({ event: e }) => {
    const t = Date.parse(e.start_time);
    return Number.isFinite(t) && t >= start && t < end;
  });
}

// ── Hub ─────────────────────────────────────────────────────────────────

function CalendarHub() {
  const { i18n } = useLingui();
  const RANGE_OPTIONS: readonly { id: CalendarRange; label: string }[] = [
    { id: "day", label: i18n._(msg`Giorno`) },
    { id: "week", label: i18n._(msg`Sett.`) },
    { id: "month", label: i18n._(msg`Mese`) },
  ];
  const navigate = useNavigate();
  const goToIntegrations = useGoToIntegrations();
  const search = useSearch({ from: "/_authed/workspace/calendar" });
  // DRY URL-patcher — same hook as conversations.tsx / documents.tsx.
  const searchNav = useSearchNavigation<{
    type: ReadonlyArray<string>;
    provider: ReadonlyArray<string>;
    period: string;
  }>("/workspace/calendar");
  // searchSchema TolerantStringSchema already coerces (May 23 DRY pass).
  const focusId = search.focus ?? null;
  const messageId = (search as { msg?: string }).msg ?? null;
  const userId = useChatUserId();
  const {
    connections,
    isLoading: loadingConn,
    isError: errorConn,
    hasAccess,
  } = useCalendarConnections();

  // View state — range + selected anchor day (drives Day view + zoom-in).
  const [range, setRangeState] = useState<CalendarRange>(() => readPersistedRange());
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

  // ``setBriefingHasContent`` still feeds ``BriefingSlot onLoaded``; the value
  // itself is no longer read (its only consumer was the CompanionHero, removed
  // with the Jul 2026 command-bar de-layering).
  const [, setBriefingHasContent] = useState(false);

  const setRange = useCallback((next: CalendarRange) => {
    setRangeState(next);
    try {
      window.localStorage.setItem(RANGE_STORAGE_KEY, next);
    } catch {
      // ignore — persistence is best-effort.
    }
  }, []);

  // Step the anchor by the active range: ±1 day / ±7 days / ±1 month. Month
  // jumps to the 1st of the target month (``setMonth`` day-arg) to avoid the
  // Jan-31 → Mar-3 overflow; the day-of-month is irrelevant to the month grid.
  const shiftAnchor = useCallback(
    (direction: -1 | 1) => {
      setSelectedDate((prev) => {
        const d = new Date(prev);
        if (range === "day") d.setDate(d.getDate() + direction);
        else if (range === "week") d.setDate(d.getDate() + direction * 7);
        else d.setMonth(d.getMonth() + direction, 1);
        return d;
      });
    },
    [range],
  );
  const goToToday = useCallback(() => setSelectedDate(new Date()), []);

  // ── Connection picker plumbing ────────────────────────────────────────
  // Map BE-shaped ``CalendarConnection`` onto the picker's
  // ``ConnectionPickerOption`` contract. Memoised so the
  // ``useActiveConnections`` reconciliation effect only runs when the
  // underlying list actually changes.
  const connectionOptions = useMemo<readonly ConnectionPickerOption[]>(
    () => connections.map(connectionToOption),
    [connections],
  );

  const {
    activeIds,
    setActiveIds,
    isReady: connsReady,
  } = useActiveConnections("calendar", connectionOptions);

  // Restrict the BE fetch + event timeline to the user's currently-active
  // accounts. Stays a *subset* of ``connections`` — passing the full BE
  // shape (not the picker option) is required by ``useUpcomingCalendarEvents``.
  const activeConnections = useMemo<readonly CalendarConnection[]>(() => {
    if (activeIds.length === 0) return [];
    const set = new Set(activeIds);
    return connections.filter((c) => set.has(c.id));
  }, [connections, activeIds]);

  // O(1) option lookup, used by the colour resolver below.
  const optionById = useMemo<Map<string, ConnectionPickerOption>>(
    () => new Map(connectionOptions.map((o) => [o.id, o])),
    [connectionOptions],
  );

  /** Resolve the brand colour for a given connection id — falls back to
   * the Ando accent when the id is null or no longer known (e.g. an event
   * survived in cache after its connection was disconnected). */
  const getConnectionColor = useCallback(
    (connectionId: string | null): string => {
      if (connectionId === null) return ANDO_ACCENT_HEX;
      const option = optionById.get(connectionId);
      if (!option) return ANDO_ACCENT_HEX;
      return resolveConnectionColor(option);
    },
    [optionById],
  );

  // Fetch exactly the window the current view renders (range + navigated
  // ``selectedDate``) — see ``rangeBounds``. This is what makes paging with
  // the arrows load the events of any month/week, not just today ± n.
  const fetchWindow = useMemo(() => {
    const { start, end } = rangeBounds(range, selectedDate);
    return { start: new Date(start).toISOString(), end: new Date(end).toISOString() };
  }, [range, selectedDate]);

  // Pass ``activeConnections`` (not ``connections``) so deselected accounts
  // stop hitting ``/calendar/events`` immediately.
  const { bundles } = useUpcomingCalendarEvents(
    activeConnections,
    fetchWindow.start,
    fetchWindow.end,
  );
  // First-load flag for the active window (no cached data yet) — gates the
  // empty-state so the "nessun evento" box never flashes before events land.
  const eventsLoading = bundles.some((b) => b.isLoading);

  // Attribute every event to its source connection. Cross-account dedup
  // (May 25, 2026 user feedback): quando l'utente ha 2+ calendari di
  // utenti DIVERSI (es. personal + assistente) lo STESSO meeting può
  // apparire in entrambi (organizer ha invitato entrambi gli account).
  // Il BE non espone ``iCalUID`` (RFC 5545) quindi dedup server-side
  // non possibile. Dedup euristico FE per ``(start, end, title)``:
  // UN'unica entry visiva, attribution multi-account preservata in
  // ``allConnectionIds`` per future "👥 N account" badge.
  const upcomingAllUnfiltered = useMemo<readonly AttributedEvent[]>(() => {
    const byKey = new Map<string, AttributedEvent>();
    for (const b of bundles) {
      for (const event of b.events) {
        const key = `${event.start_time}|${event.end_time}|${event.title.trim().toLowerCase()}`;
        const existing = byKey.get(key);
        if (existing) {
          if (!existing.allConnectionIds.includes(b.connection.id)) {
            byKey.set(key, {
              event: existing.event,
              connectionId: existing.connectionId,
              allConnectionIds: [...existing.allConnectionIds, b.connection.id],
            });
          }
          continue;
        }
        byKey.set(key, {
          event,
          connectionId: b.connection.id,
          allConnectionIds: [b.connection.id],
        });
      }
    }
    const flat = Array.from(byKey.values());
    flat.sort((a, b) => Date.parse(a.event.start_time) - Date.parse(b.event.start_time));
    return flat;
  }, [bundles]);

  // Inline-viewer branch: when ``?focus=<event_id>`` is in the URL, we
  // mount the ``CalendarEventPane`` IN PLACE of the week grid. Same
  // pattern as documents.tsx / conversations.tsx — single Opzione B
  // discipline across all hubs.
  const focusedEvent = useMemo<CalendarEvent | null>(() => {
    if (!focusId) return null;
    return upcomingAllUnfiltered.find(({ event: e }) => e.event_id === focusId)?.event ?? null;
  }, [focusId, upcomingAllUnfiltered]);

  // Range-scoped event window for the active view.
  const eventsInRange = useMemo(
    () => filterByRange(upcomingAllUnfiltered, range, selectedDate),
    [upcomingAllUnfiltered, range, selectedDate],
  );

  // The user's NEXT meeting inside the current (view-scoped) window: first
  // event ≥ now. Only surfaced as the pulsing highlight / "prossimo tra" when
  // today's period is on screen — while browsing another month the earliest
  // event there is NOT "your next meeting", so we don't pulse it.
  const viewingToday = isTodayVisible(range, selectedDate);
  const nextEvent: CalendarEvent | null = useMemo(() => {
    const now = Date.now();
    return (
      upcomingAllUnfiltered.find(({ event: e }) => {
        const t = Date.parse(e.start_time);
        return !Number.isNaN(t) && t >= now;
      })?.event ?? null
    );
  }, [upcomingAllUnfiltered]);
  const highlightEventId = viewingToday ? (nextEvent?.event_id ?? null) : null;

  // Group events by day-key for column rendering — keyed on the filtered set
  // for Week/Month, on the FULL feed for Day so the timeline shows everything.
  const gridSource = range === "day" ? upcomingAllUnfiltered : eventsInRange;
  const untitledLabel = i18n._(msg`Senza titolo`);
  const gridByDay: Record<string, GridEvent[]> = useMemo(
    () => buildGridByDay(gridSource, highlightEventId, getConnectionColor, untitledLabel),
    [gridSource, highlightEventId, getConnectionColor, untitledLabel],
  );

  // ── Header ────────────────────────────────────────────────────────────
  const week = useMemo(() => buildWeek(selectedDate), [selectedDate]);
  const month = useMemo(() => buildMonth(selectedDate), [selectedDate]);

  const totalEvents = eventsInRange.length;
  const minutesToNext = useMemo(() => {
    if (!viewingToday || !nextEvent) return null;
    const t = Date.parse(nextEvent.start_time);
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.round((t - Date.now()) / 60_000));
  }, [viewingToday, nextEvent]);

  // True once every loader has settled AND the selected period really has
  // nothing in it — drives the "Nessun evento" note in the command bar (the
  // grid itself always renders, so the user keeps the calendar structure).
  const isRangeEmpty = connsReady && !loadingConn && !eventsLoading && totalEvents === 0;

  // Zero is never spelled out here — the "Nessun evento" note below says it
  // once. Keyed on the count alone (not ``isRangeEmpty``) so "0 eventi" can't
  // flash for a frame while the events query is still in flight.
  const meta = loadingConn ? (
    <span className="text-ink-3">
      <Trans>Caricamento…</Trans>
    </span>
  ) : totalEvents === 0 ? null : (
    <>
      <strong>
        <Trans>
          <span className="tabular-nums">{totalEvents}</span> eventi
        </Trans>
      </strong>
      {minutesToNext !== null ? (
        <>
          {" "}
          ·{" "}
          <span className="font-medium text-accent">
            <Trans>
              prossimo tra{" "}
              <span className="tabular-nums">{formatTimeUntil(minutesToNext, i18n)}</span>
            </Trans>
          </span>
        </>
      ) : null}
    </>
  );

  // Empty / no-access states
  const noConnections = !loadingConn && connections.length === 0;
  const showOauthPrompt = (!hasAccess && userId !== null) || noConnections;

  // ── Day cell click → zoom into Day view for that date ─────────────────
  const handleDayClick = useCallback(
    (iso: string) => {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return;
      setSelectedDate(d);
      setRange("day");
    },
    [setRange],
  );

  const handleEventClick = useCallback(
    (eventId: string) => {
      navigate({ to: "/workspace/calendar", search: { focus: eventId } });
    },
    [navigate],
  );

  // ── Inline-viewer mode (Opzione B) ───────────────────────────────────
  if (focusId && focusedEvent) {
    return (
      <FocusedCalendarView
        event={focusedEvent}
        focusId={focusId}
        messageId={messageId}
        onBack={() =>
          navigate({
            to: "/workspace/calendar",
            search: clearFocusKeepMsg(messageId),
          })
        }
      />
    );
  }
  if (focusId && !focusedEvent && loadingConn) {
    return <FocusedCalendarLoading focusId={focusId} messageId={messageId} />;
  }
  if (focusId && !focusedEvent && !loadingConn) {
    return (
      <FocusedCalendarMissing
        focusId={focusId}
        messageId={messageId}
        onBack={() =>
          navigate({
            to: "/workspace/calendar",
            search: clearFocusKeepMsg(messageId),
          })
        }
      />
    );
  }

  return (
    <SurfaceLayout
      header={
        // Jul 2026 — single-row command bar. Giorno·Settimana·Mese IS the
        // calendar's primary view switcher → it lives in the always-visible
        // ``views`` zone (pencil segmented), identical to every other hub.
        <SurfaceCommandBar
          views={
            <div className="flex items-center gap-3">
              <SegmentedTabs
                ariaLabel={i18n._(msg`Vista calendario`)}
                options={RANGE_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
                value={range}
                onChange={setRange}
              />
              <CalendarDateNav
                range={range}
                anchor={selectedDate}
                onPrev={() => shiftAnchor(-1)}
                onNext={() => shiftAnchor(1)}
                onToday={goToToday}
              />
            </div>
          }
          count={meta}
          note={isRangeEmpty ? <Trans>Nessun evento</Trans> : undefined}
          search={
            <WorkspaceSearchBar
              placeholder={i18n._(msg`Cerca eventi, riunioni…`)}
              ariaLabel={i18n._(msg`Cerca calendario`)}
              initialQuery={search.q}
              onSearch={searchNav.setQuery}
            />
          }
          filters={
            <SurfaceFiltersPopover
              ariaLabel={i18n._(msg`Filtri calendario`)}
              label={<Trans>Filtri</Trans>}
              activeCount={
                (search.type?.length ?? 0) +
                (search.provider?.length ?? 0) +
                ((search.period ?? "all") !== "all" ? 1 : 0)
              }
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <CalendarSearchFilters
                  type={search.type ?? []}
                  provider={search.provider ?? []}
                  period={search.period ?? "all"}
                  onChange={searchNav.patchFilters}
                />
              </div>
            </SurfaceFiltersPopover>
          }
          actions={
            <>
              {connectionOptions.length > 0 ? (
                <ConnectionPicker
                  connections={connectionOptions}
                  activeIds={activeIds}
                  onChange={setActiveIds}
                  label={
                    connectionOptions.length === 1
                      ? i18n._(msg`1 calendario`)
                      : i18n._(msg`${activeIds.length}/${connectionOptions.length} calendari`)
                  }
                  ariaLabel={i18n._(msg`Seleziona calendari attivi`)}
                />
              ) : null}
              <NewActionMenu
                area="calendar"
                label={i18n._(msg`Nuovo`)}
                extraItems={[
                  {
                    id: "calendar:configure_connectors",
                    label: i18n._(msg`Configura connettori`),
                    icon: Settings2,
                    onSelect: () => goToIntegrations("calendar"),
                  },
                ]}
              />
            </>
          }
        />
      }
    >
      {/* Briefing slot — proactive "Brief & Deep" card for the calendar
          domain. Silent on BE 404 / empty payload, so the existing surface
          chrome stays intact across BE rollouts. */}
      <BriefingSlot domain="calendar" className="mb-5" onLoaded={setBriefingHasContent} />
      {errorConn || showOauthPrompt ? (
        <CalendarEmptyOnboarding
          errored={errorConn}
          onConnect={() => goToIntegrations("calendar")}
        />
      ) : !connsReady ? // Hold the event area blank until ``useActiveConnections`` finishes
      // hydrating from localStorage — prevents a flash of "all events"
      // before the user's saved filter kicks in. Header + hero stay visible.
      // An empty period keeps its grid too (a real calendar shows the date
      // structure): the "Nessun evento" note in the command bar says it.
      null : range === "day" ? (
        <DayView
          date={selectedDate}
          events={gridByDay[dateKey(selectedDate)] ?? []}
          onEventClick={handleEventClick}
        />
      ) : range === "week" ? (
        <WeekView
          week={week}
          gridByDay={gridByDay}
          onEventClick={handleEventClick}
          onDayClick={handleDayClick}
        />
      ) : (
        <MonthView
          month={month}
          gridByDay={gridByDay}
          onEventClick={handleEventClick}
          onDayClick={handleDayClick}
        />
      )}
    </SurfaceLayout>
  );
}

// ── Date navigation (prev/next + period title + Oggi) ───────────────────

const CalendarDateNav: React.FC<{
  readonly range: CalendarRange;
  readonly anchor: Date;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onToday: () => void;
}> = ({ range, anchor, onPrev, onNext, onToday }) => {
  const { i18n } = useLingui();
  const prevLabel =
    range === "day"
      ? i18n._(msg`Giorno precedente`)
      : range === "week"
        ? i18n._(msg`Settimana precedente`)
        : i18n._(msg`Mese precedente`);
  const nextLabel =
    range === "day"
      ? i18n._(msg`Giorno successivo`)
      : range === "week"
        ? i18n._(msg`Settimana successiva`)
        : i18n._(msg`Mese successivo`);
  const todayVisible = isTodayVisible(range, anchor);
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label={prevLabel}
        onClick={onPrev}
      >
        <ChevronLeft aria-hidden />
      </Button>
      <span
        aria-live="polite"
        className="min-w-[8rem] px-1 text-center font-semibold text-[13px] text-ink-1 tabular-nums"
      >
        {formatRangeTitle(range, anchor)}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label={nextLabel}
        onClick={onNext}
      >
        <ChevronRight aria-hidden />
      </Button>
      <Button variant="ghost" size="sm" className="ml-1" onClick={onToday} disabled={todayVisible}>
        <Trans>Oggi</Trans>
      </Button>
    </div>
  );
};
CalendarDateNav.displayName = "CalendarDateNav";

// ── Time-grid model (Week; Day reuses the hour bounds) ──────────────────

const DAY_HOUR_START = 8;
const DAY_HOUR_END = 22;
const DAY_HOURS: readonly number[] = Array.from(
  { length: DAY_HOUR_END - DAY_HOUR_START + 1 },
  (_, i) => DAY_HOUR_START + i,
);
/** Pixel height of one hour row in the time grid. */
const HOUR_PX = 48;
/** Floor height so a short meeting stays readable / clickable. */
const MIN_EVENT_PX = 18;
/** Drawable height of the grid body: 08:00 → 22:00. */
const GRID_HEIGHT_PX = (DAY_HOUR_END - DAY_HOUR_START) * HOUR_PX;

/** Minutes-from-midnight → Y offset (px), clamped to the visible band. */
function minutesToY(min: number): number {
  const clamped = Math.min(DAY_HOUR_END * 60, Math.max(DAY_HOUR_START * 60, min));
  return ((clamped - DAY_HOUR_START * 60) / 60) * HOUR_PX;
}

interface PositionedEvent {
  readonly ev: GridEvent;
  readonly top: number;
  readonly height: number;
  readonly leftPct: number;
  readonly widthPct: number;
}

/**
 * Lay out one day's events as absolutely-positioned blocks: top/height from
 * start/duration, and side-by-side columns for overlapping meetings (classic
 * interval-partition — one lane per concurrent event within an overlap cluster).
 */
function layoutDayColumn(events: readonly GridEvent[]): readonly PositionedEvent[] {
  const items = events
    .map((ev) => {
      const s = Date.parse(ev.raw.start_time);
      const e = Date.parse(ev.raw.end_time);
      const startMin = Number.isFinite(s)
        ? new Date(s).getHours() * 60 + new Date(s).getMinutes()
        : DAY_HOUR_START * 60;
      let endMin = Number.isFinite(e)
        ? new Date(e).getHours() * 60 + new Date(e).getMinutes()
        : startMin + 60;
      if (endMin <= startMin) endMin = startMin + 30;
      return { ev, startMin, endMin };
    })
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const out: PositionedEvent[] = [];
  let cluster: { ev: GridEvent; startMin: number; endMin: number }[] = [];
  let clusterEnd = -1;

  const flush = () => {
    const colEnds: number[] = []; // running end-time per lane
    const placed = cluster.map((it) => {
      let col = colEnds.findIndex((end) => end <= it.startMin);
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(it.endMin);
      } else {
        colEnds[col] = it.endMin;
      }
      return { it, col };
    });
    const cols = colEnds.length || 1;
    for (const { it, col } of placed) {
      const top = minutesToY(it.startMin);
      out.push({
        ev: it.ev,
        top,
        height: Math.max(MIN_EVENT_PX, minutesToY(it.endMin) - top),
        leftPct: (col / cols) * 100,
        widthPct: (1 / cols) * 100,
      });
    }
    cluster = [];
    clusterEnd = -1;
  };

  for (const it of items) {
    if (cluster.length > 0 && it.startMin >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = cluster.length === 1 ? it.endMin : Math.max(clusterEnd, it.endMin);
  }
  if (cluster.length > 0) flush();
  return out;
}

// ── View: Week (time grid — hour gutter + 7 day columns) ────────────────

interface WeekViewProps {
  readonly week: readonly DayCell[];
  readonly gridByDay: Record<string, GridEvent[]>;
  readonly onEventClick: (eventId: string) => void;
  readonly onDayClick: (iso: string) => void;
}

const WeekView: React.FC<WeekViewProps> = ({ week, gridByDay, onEventClick, onDayClick }) => {
  const { i18n } = useLingui();
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nowVisible = nowMin >= DAY_HOUR_START * 60 && nowMin <= DAY_HOUR_END * 60;
  const nowY = minutesToY(nowMin);
  const hasToday = week.some((d) => d.today);

  // Lay out each day once; reused for rendering AND the auto-scroll target.
  const positionedByDay = useMemo(() => {
    const m: Record<string, readonly PositionedEvent[]> = {};
    for (const d of week) m[d.key] = layoutDayColumn(gridByDay[d.key] ?? []);
    return m;
  }, [week, gridByDay]);

  // Top (px) of the earliest event in the week — the initial scroll target.
  const earliestTop = useMemo(() => {
    let min = Number.POSITIVE_INFINITY;
    for (const d of week) {
      for (const p of positionedByDay[d.key] ?? []) if (p.top < min) min = p.top;
    }
    return Number.isFinite(min) ? min : null;
  }, [week, positionedByDay]);

  // Auto-scroll the grid so the relevant slice is visible on load: to "now"
  // when today is in view, otherwise the caller falls back to the earliest
  // event. Fixes evening events (e.g. 21:00) sitting below the fold. The
  // "now" target is read via ref so the minute-tick never re-scrolls (and
  // never fights a manual scroll); the effect re-runs only on week change or
  // when events arrive (``earliestTop``).
  const scrollRef = useRef<HTMLDivElement>(null);
  const nowTargetRef = useRef(Number.NaN);
  nowTargetRef.current = hasToday && nowVisible ? nowY - 100 : Number.NaN;
  const weekKey = week[0]?.key ?? "";
  // biome-ignore lint/correctness/useExhaustiveDependencies: weekKey is an intentional trigger (re-scroll on week change); the "now" target is read via ref on purpose so the minute-tick never re-scrolls.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const target = !Number.isNaN(nowTargetRef.current)
      ? nowTargetRef.current
      : earliestTop !== null
        ? earliestTop - 60
        : minutesToY(9 * 60);
    el.scrollTop = Math.max(0, target);
  }, [weekKey, earliestTop]);

  return (
    <div className="-mx-8 mt-2 px-3 pb-4">
      {/* Day header row — OUTSIDE the scroller: nothing ever slides underneath
          it, so it needs no opaque plate over the translucent pane. ``pr-1.5``
          mirrors the 6px scrollbar gutter reserved below, keeping the labels
          centred on their columns. Gutter spacer aligns it over the columns. */}
      <div className="grid grid-cols-[3rem_repeat(7,minmax(0,1fr))] border-black/5 border-b py-1 pr-1.5">
        <div aria-hidden />
        {week.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => onDayClick(d.iso)}
            aria-label={i18n._(msg`Apri ${d.day} ${d.date}`)}
            // Same box geometry for every day — only the fill changes — so the
            // hover rectangle lands exactly where today's chip sits.
            className={`mx-1 cursor-pointer rounded-lg px-2 py-1.5 text-center transition-colors ${
              d.today
                ? "bg-[rgba(255,122,26,0.08)] hover:bg-[rgba(255,122,26,0.14)]"
                : "hover:bg-black/[0.03]"
            }`}
          >
            <div
              className={
                d.today
                  ? "font-semibold text-[10px] text-accent uppercase tracking-[0.05em]"
                  : `font-medium text-[10px] uppercase tracking-[0.05em] ${
                      d.weekend ? "text-ink-3/50" : "text-ink-3"
                    }`
              }
            >
              {d.day}
            </div>
            <div
              className={
                d.today
                  ? "mt-0.5 font-bold text-[14px] text-accent tabular-nums"
                  : `mt-0.5 font-medium text-[14px] tabular-nums ${
                      d.weekend ? "text-ink-3/50" : "text-ink-1"
                    }`
              }
            >
              {d.date}
            </div>
          </button>
        ))}
      </div>
      <div
        ref={scrollRef}
        className="max-h-[calc(100vh-16rem)] min-h-[24rem] overflow-y-auto [scrollbar-gutter:stable]"
      >
        {/* Time grid — hour gutter + 7 relative day columns. */}
        <div className="grid grid-cols-[3rem_repeat(7,minmax(0,1fr))] pt-2">
          <div className="relative" style={{ height: GRID_HEIGHT_PX }}>
            {DAY_HOURS.map((h, idx) => (
              <div
                key={h}
                className="-translate-y-1/2 absolute right-1.5 font-medium text-[10px] text-ink-3 tabular-nums"
                style={{ top: idx * HOUR_PX }}
              >
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          {week.map((d) => (
            <div
              key={d.key}
              className={`relative border-black/5 border-l ${
                d.today ? "bg-[rgba(255,122,26,0.035)]" : ""
              }`}
              style={{ height: GRID_HEIGHT_PX }}
            >
              {DAY_HOURS.map((h, idx) => (
                <div
                  key={h}
                  aria-hidden
                  className="absolute inset-x-0 border-black/[0.06] border-t"
                  style={{ top: idx * HOUR_PX }}
                />
              ))}
              {(positionedByDay[d.key] ?? []).map((p) => (
                <WeekGridEvent key={p.ev.id} p={p} onClick={onEventClick} />
              ))}
              {d.today && nowVisible ? (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 z-10"
                  style={{ top: nowY }}
                >
                  <div className="relative h-px bg-ando-orange">
                    <span className="-left-1 -top-[3px] absolute h-[7px] w-[7px] rounded-full bg-ando-orange" />
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
WeekView.displayName = "WeekView";

// ── Week time-grid event block ──────────────────────────────────────────

const WeekGridEvent: React.FC<{
  readonly p: PositionedEvent;
  readonly onClick: (eventId: string) => void;
}> = ({ p, onClick }) => {
  const { ev, top, height, leftPct, widthPct } = p;
  const compact = height < 34;
  return (
    <button
      type="button"
      onClick={() => onClick(ev.id)}
      style={{
        top,
        height,
        left: `calc(${leftPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
        borderLeftColor: ev.color,
      }}
      className={`absolute overflow-hidden rounded-md border border-black/5 border-l-[3px] bg-white/90 px-1.5 py-0.5 text-left shadow-[0_1px_2px_rgba(60,40,20,0.05)] transition-shadow hover:shadow-[0_2px_6px_rgba(60,40,20,0.1)] ${
        ev.highlight ? "ring-2 ring-ando-orange/45" : ""
      }`}
    >
      <div className="truncate font-medium text-[10px] text-ink-1 leading-tight">{ev.title}</div>
      {!compact ? (
        <div className="truncate text-[9px] text-ink-3 tabular-nums">{ev.time}</div>
      ) : null}
    </button>
  );
};
WeekGridEvent.displayName = "WeekGridEvent";

// ── View: Day (vertical hour ruler 8-22) ────────────────────────────────

interface DayViewProps {
  readonly date: Date;
  readonly events: readonly GridEvent[];
  readonly onEventClick: (eventId: string) => void;
}

const DayView: React.FC<DayViewProps> = ({ events, onEventClick }) => {
  // Bucket events by hour-of-day for placement against the ruler.
  const byHour = useMemo(() => {
    const map: Record<number, GridEvent[]> = {};
    for (const ev of events) {
      const t = Date.parse(ev.raw.start_time);
      if (Number.isNaN(t)) continue;
      const h = new Date(t).getHours();
      const bucket = h < DAY_HOUR_START ? DAY_HOUR_START : h > DAY_HOUR_END ? DAY_HOUR_END : h;
      if (!map[bucket]) map[bucket] = [];
      map[bucket].push(ev);
    }
    return map;
  }, [events]);

  return (
    <div className="-mx-8 mt-2 px-3 pb-4">
      <div className="flex flex-col">
        {DAY_HOURS.map((h) => {
          const hourEvents = byHour[h] ?? [];
          return (
            <div
              key={h}
              className="flex min-h-[52px] items-start gap-3 border-black/5 border-t pt-1.5 first:border-t-0"
            >
              <div className="w-12 shrink-0 pt-0.5 text-right font-medium text-[10px] text-ink-3 tabular-nums">
                {String(h).padStart(2, "0")}:00
              </div>
              <div className="flex flex-1 flex-col gap-1 pb-1.5">
                {hourEvents.map((ev) => (
                  <EventChip key={ev.id} event={ev} onClick={onEventClick} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
DayView.displayName = "DayView";

// ── View: Month (6×7 grid) ──────────────────────────────────────────────

interface MonthViewProps {
  readonly month: readonly DayCell[];
  readonly gridByDay: Record<string, GridEvent[]>;
  readonly onEventClick: (eventId: string) => void;
  readonly onDayClick: (iso: string) => void;
}

const MAX_MONTH_CELL_EVENTS = 3;

const MonthView: React.FC<MonthViewProps> = ({ month, gridByDay, onEventClick, onDayClick }) => {
  const { i18n } = useLingui();
  return (
    <div className="-mx-8 mt-2 px-3 pb-4">
      {/* Weekday header strip */}
      <div className="grid grid-cols-7 gap-1 pb-1">
        {DAY_LABELS_IT.map((label, i) => (
          <div
            key={label}
            className={`text-center font-medium text-[10px] uppercase tracking-[0.05em] ${
              i >= 5 ? "text-ink-3/50" : "text-ink-3"
            }`}
          >
            {label}
          </div>
        ))}
      </div>
      {/* 6×7 grid */}
      <div className="grid grid-cols-7 grid-rows-6 gap-1">
        {month.map((d) => {
          const events = gridByDay[d.key] ?? [];
          const visible = events.slice(0, MAX_MONTH_CELL_EVENTS);
          const overflow = events.length - visible.length;
          return (
            <div
              key={`${d.key}-${d.inMonth ? "in" : "out"}`}
              className={`flex min-h-[88px] flex-col rounded-lg border p-1 transition-colors ${
                d.today
                  ? "border-[rgba(255,122,26,0.45)] bg-[rgba(255,122,26,0.04)]"
                  : d.inMonth
                    ? "border-hairline/60 bg-transparent hover:bg-cream"
                    : "border-black/5 bg-black/[0.015] text-ink-3/60"
              }`}
            >
              <button
                type="button"
                onClick={() => onDayClick(d.iso)}
                className="self-start rounded px-1 text-left transition-colors hover:bg-black/[0.04]"
                aria-label={i18n._(
                  msg`Apri ${d.date} ${MONTHS_IT[new Date(d.iso).getMonth()] ?? ""}`,
                )}
              >
                <span
                  className={
                    d.today
                      ? "font-bold text-[12px] text-accent tabular-nums"
                      : `font-medium text-[12px] tabular-nums ${
                          d.inMonth ? "text-ink-1" : "text-ink-3/60"
                        }`
                  }
                >
                  {d.date}
                </span>
              </button>
              <div className="mt-1 flex flex-1 flex-col gap-0.5">
                {visible.map((ev) => (
                  <button
                    key={ev.id}
                    type="button"
                    onClick={() => onEventClick(ev.id)}
                    // Same left-border colour hint as ``EventChip`` so the
                    // Month view stays visually consistent with Week/Day.
                    style={{
                      borderLeftColor: ev.color,
                      borderLeftWidth: 3,
                      borderLeftStyle: "solid",
                    }}
                    className={
                      ev.highlight
                        ? "truncate rounded px-1 py-0.5 pl-1.5 text-left font-medium text-[10px] text-accent transition-colors hover:bg-[rgba(255,122,26,0.12)]"
                        : "truncate rounded px-1 py-0.5 pl-1.5 text-left text-[10px] text-ink-2 transition-colors hover:bg-black/[0.04]"
                    }
                  >
                    <span className="tabular-nums">{ev.time}</span>{" "}
                    <span className="text-ink-1">{ev.title}</span>
                  </button>
                ))}
                {overflow > 0 ? (
                  <button
                    type="button"
                    onClick={() => onDayClick(d.iso)}
                    className="self-start rounded px-1 py-0.5 font-medium text-[10px] text-ink-3 transition-colors hover:text-ink-2"
                  >
                    <Trans>+{overflow} altri</Trans>
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
MonthView.displayName = "MonthView";

// ── Shared event chip ──────────────────────────────────────────────────

const EventChip: React.FC<{
  readonly event: GridEvent;
  readonly onClick: (eventId: string) => void;
}> = ({ event: ev, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(ev.id)}
    // 3px left border tinted with the source-connection colour — Google
    // blue / Microsoft blue / per-connection override / Ando accent fallback.
    style={{ borderLeftColor: ev.color, borderLeftWidth: 3, borderLeftStyle: "solid" }}
    className={
      ev.highlight
        ? "hover:-translate-y-px relative cursor-pointer rounded-lg border border-[rgba(255,122,26,0.45)] bg-cream p-2.5 text-left shadow-[0_0_0_3px_rgba(255,122,26,0.12)] transition-transform"
        : "hover:-translate-y-px cursor-pointer rounded-lg border border-hairline/60 bg-cream p-2.5 text-left transition-transform"
    }
  >
    {ev.highlight ? (
      <>
        {/* Outer pulsing ring — slowed to 3s so the rhythm
            feels organic, not compulsive. `animate-ping`
            default is 1s; we override with explicit duration. */}
        <span
          aria-hidden
          style={{ animationDuration: "3.2s" }}
          className="-inset-1 pointer-events-none absolute animate-ping rounded-xl border-2 border-ando-orange/55 motion-reduce:hidden"
        />
        {/* Pulsing dot top-right — slowed pulse, 3.6s. */}
        <span
          aria-hidden
          style={{ animationDuration: "3.6s" }}
          className="absolute top-1.5 right-1.5 h-2 w-2 animate-pulse rounded-full bg-ando-orange motion-reduce:animate-none"
        />
      </>
    ) : null}
    <div
      className={
        ev.highlight
          ? "font-semibold text-[10px] text-accent tabular-nums"
          : "text-[10px] text-ink-3 tabular-nums"
      }
    >
      {ev.time}
    </div>
    <div
      className={
        ev.highlight
          ? "mt-0.5 font-semibold text-[11px] text-ink-1 leading-tight"
          : "mt-0.5 font-medium text-[11px] text-ink-1 leading-tight"
      }
    >
      {ev.title}
    </div>
    {ev.meta ? <div className="mt-0.5 truncate text-[9px] text-ink-3">{ev.meta}</div> : null}
  </button>
);
EventChip.displayName = "EventChip";

// ── Empty / onboarding state ─────────────────────────────────────────────

interface CalendarEmptyOnboardingProps {
  /** True when the connections endpoint itself errored — copy adapts. */
  readonly errored: boolean;
  readonly onConnect: () => void;
}

/**
 * Info-rich empty state for users without any calendar connection.
 * Replaces the previous bare ``SurfaceEmptyState`` with a layout that
 * tells the user WHY connecting matters and HOW to do it. The
 * "Cosa potrai fare" bullets reflect the real Ando capabilities that
 * land once the OAuth handshake completes.
 */
const CalendarEmptyOnboarding: React.FC<CalendarEmptyOnboardingProps> = ({
  errored,
  onConnect,
}) => {
  const { i18n } = useLingui();
  return (
    <SurfaceEmptyState
      icon={<CalendarIcon size={18} strokeWidth={1.75} />}
      title={
        errored
          ? i18n._(msg`Impossibile caricare il calendario`)
          : i18n._(msg`Connetti il tuo calendario`)
      }
      description={
        errored
          ? i18n._(
              msg`Verifica la connessione e riprova. Oppure collega un calendario per attivare la vista.`,
            )
          : i18n._(
              msg`Collega Google Calendar o Outlook per vedere meeting e brief preparati da Ando.`,
            )
      }
      action={
        <div className="flex flex-col items-center gap-4">
          <Button variant="primary" size="md" onClick={onConnect}>
            <LinkIcon aria-hidden />
            <Trans>Collega calendario</Trans>
          </Button>
          <div className="rounded-lg border border-hairline bg-white/55 px-4 py-3 text-left">
            <div className="mb-2 flex items-center gap-1.5 font-medium text-[10px] text-ando-orange uppercase tracking-[0.05em]">
              <Sparkles className="h-3 w-3" strokeWidth={2.25} aria-hidden />
              <Trans>Cosa potrai fare</Trans>
            </div>
            <ul className="space-y-1.5 text-[12px] text-ink-2">
              <li className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ando-orange"
                />
                <span>
                  <Trans>Brief automatici prima dei meeting</Trans>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ando-orange"
                />
                <span>
                  <Trans>Riassunto degli eventi della settimana</Trans>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ando-orange"
                />
                <span>
                  <Trans>Notifiche sugli slot liberi</Trans>
                </span>
              </li>
            </ul>
          </div>
        </div>
      }
    />
  );
};
CalendarEmptyOnboarding.displayName = "CalendarEmptyOnboarding";

// ── Inline-viewer sub-components ─────────────────────────────────────────
//
// All three branches compose ``<FocusedViewLayout>`` — shared chrome
// (back chevron + tab eyebrow + SourceRail + animated viewer slot). Per
// the May 2026 DRY discipline; mirror of ``documents.tsx`` /
// ``conversations.tsx``.

const CAL_BACK_LABEL_MSG = msg`Torna al calendario`;

// "HH:MM" / "Domani 09:00" etc — pulled up from CalendarEventPane so
// the FocusedViewLayout can surface the same range in its rich header
// eyebrow + subtitle. DRY: see CalendarEventPane for the original
// helpers; copied here as 3 tiny pure functions, not imported, to avoid
// importing the entire pane just for two formatters.
function formatTimeRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  const startTime = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
  const endTime = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()
  ) {
    return `${start.getDate()} ${MONTHS_IT_SHORT[start.getMonth()]} · ${startTime} — ${endTime}`;
  }
  return `${start.getDate()} ${MONTHS_IT_SHORT[start.getMonth()]} ${startTime} — ${end.getDate()} ${MONTHS_IT_SHORT[end.getMonth()]} ${endTime}`;
}

function formatRelativeUpcoming(
  iso: string,
  i18n: ReturnType<typeof useLingui>["i18n"],
): string | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const delta = t - Date.now();
  if (delta < 0) return null;
  const MS_MIN = 60_000;
  const MS_HOUR = 60 * MS_MIN;
  const MS_DAY = 24 * MS_HOUR;
  if (delta < MS_MIN) return i18n._(msg`Ora`);
  if (delta < MS_HOUR) return i18n._(msg`Tra ${Math.floor(delta / MS_MIN)} minuti`);
  if (delta < MS_DAY) return i18n._(msg`Tra ${Math.floor(delta / MS_HOUR)} ore`);
  if (delta < 2 * MS_DAY) return i18n._(msg`Domani`);
  if (delta < 7 * MS_DAY) return i18n._(msg`Tra ${Math.floor(delta / MS_DAY)} giorni`);
  return null;
}

const FocusedCalendarView: React.FC<{
  event: CalendarEvent;
  focusId: string;
  messageId: string | null;
  onBack: () => void;
}> = ({ event, focusId, messageId, onBack }) => {
  const { i18n } = useLingui();
  const eyebrowSuffix = formatRelativeUpcoming(event.start_time, i18n) ?? undefined;
  const rangeLabel = formatTimeRange(event.start_time, event.end_time);
  return (
    <FocusedViewLayout
      tabLabel={i18n._(msg`Calendario`)}
      eyebrowSuffix={eyebrowSuffix}
      title={event.title || i18n._(msg`Senza titolo`)}
      subtitle={
        rangeLabel ? (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Clock className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden />
            {rangeLabel}
          </span>
        ) : undefined
      }
      icon={<CalendarIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />}
      backAriaLabel={i18n._(CAL_BACK_LABEL_MSG)}
      onBack={onBack}
      messageId={messageId}
      focusId={focusId}
    >
      <CalendarEventPane event={event} />
    </FocusedViewLayout>
  );
};

const FocusedCalendarLoading: React.FC<{
  focusId: string;
  messageId: string | null;
}> = ({ focusId, messageId }) => {
  const { i18n } = useLingui();
  return (
    <FocusedViewLayout
      tabLabel={i18n._(msg`Calendario`)}
      title={i18n._(msg`Caricamento evento…`)}
      backAriaLabel={i18n._(CAL_BACK_LABEL_MSG)}
      messageId={messageId}
      focusId={focusId}
    >
      <div className="grid h-full place-items-center px-6 py-12 text-center">
        <p className="text-[12px] text-ink-3" aria-busy>
          <Trans>Carico l'evento…</Trans>
        </p>
      </div>
    </FocusedViewLayout>
  );
};

const FocusedCalendarMissing: React.FC<{
  focusId: string;
  messageId: string | null;
  onBack: () => void;
}> = ({ focusId, messageId, onBack }) => {
  const { i18n } = useLingui();
  return (
    <FocusedViewLayout
      tabLabel={i18n._(msg`Calendario`)}
      title={i18n._(msg`Evento non trovato`)}
      backAriaLabel={i18n._(CAL_BACK_LABEL_MSG)}
      onBack={onBack}
      messageId={messageId}
      focusId={focusId}
    >
      <SurfaceEmptyState
        icon={<CalendarIcon size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Questo evento non è disponibile`)}
        description={i18n._(
          msg`Potrebbe essere stato cancellato o spostato fuori dalla finestra visibile. Torna al calendario per cercarlo.`,
        )}
        action={
          <Button variant="primary" size="md" onClick={onBack}>
            <ArrowLeft aria-hidden />
            <Trans>Torna al calendario</Trans>
          </Button>
        }
      />
    </FocusedViewLayout>
  );
};
